import React from 'react';

import Table from 'cccisd-table';
import HiddenBlock from 'cccisd-hidden-block';
import Tooltip from 'cccisd-tooltip';
import _get from 'lodash/get';

import { getPlanStatus } from '../helpers.js';

import style from './style.css';

const YouthSelectionTable = props => {
    const { youthList, setSelectedYouth, youthSelectionTableSelectedRows, youthPlans } = props;

    const DOES_NOT_RECEIVE_MESSAGES_COLOR = '#ff000052';

    const getInegligibleYouth = () => {
        let ineligible = youthList
            .map(youth => {
                let reason = '';

                const noneReceiveMessages =
                    !youth.receiveMessages &&
                    youth.caregiverList.filter(caregiver => caregiver.fields.receiveMessages).length === 0;

                const youthActivePlan = _get(youthPlans, [youth.pawnId], []).filter(plan => {
                    const status = getPlanStatus(plan);
                    return status === 'Active' || status === 'Scheduled';
                });

                if (noneReceiveMessages) {
                    reason = 'Does not receive messages';
                }
                if (youthActivePlan.length > 0) {
                    if (reason.length > 0) {
                        reason += ' & ';
                    }
                    reason += 'Ongoing active plan';
                }

                youth.ineligible_reason = reason;

                return youth;
            })
            .filter(youth => youth.ineligible_reason);

        return ineligible;
    };

    return (
        <div>
            <Table
                rowKey="pawnId"
                selectedRows={youthSelectionTableSelectedRows}
                columns={[
                    {
                        name: 'wrapId',
                        label: 'Youth Wrap ID',
                        sort: true,
                        filter: true,
                    },
                    {
                        name: 'firstName',
                        label: 'Youth First Name',
                        sort: true,
                        filter: true,
                    },
                    {
                        name: 'lastName',
                        label: 'Youth Last Name',
                        sort: true,
                        filter: true,
                    },
                    {
                        name: 'receiveMessages',
                        label: 'Youth Receives Messages',
                        sort: true,
                        render: val => {
                            return (
                                <Tooltip
                                    title="Message settings can be changed for each user when editing them in the Manage tab."
                                    placement="top"
                                >
                                    <div
                                        style={{
                                            background: !val ? DOES_NOT_RECEIVE_MESSAGES_COLOR : 'yellowgreen',
                                        }}
                                        className={style.receive_messages_value_background}
                                    >
                                        {val ? 'Yes' : 'No'}
                                    </div>
                                </Tooltip>
                            );
                        },
                    },
                    {
                        name: 'youthStatus',
                        label: 'Youth Status',
                        sort: true,
                        filter: true,
                    },
                    {
                        name: 'caregiverList',
                        label: 'Caregiver(s)',
                        sort: true,
                        filter: true,
                        render: val => {
                            return (
                                <ul>
                                    {val.map((caregiver, i) => (
                                        <li key={i}>
                                            <div>
                                                {`${caregiver.fields.firstName} ${caregiver.fields.lastName} (${caregiver.fields.wrapId})`}
                                            </div>
                                            <div>
                                                Receives Messages:{' '}
                                                <Tooltip
                                                    title="Message settings can be changed for each user when editing them in the Manage tab."
                                                    placement="top"
                                                >
                                                    <span
                                                        style={{
                                                            background: !caregiver.fields.receiveMessages
                                                                ? DOES_NOT_RECEIVE_MESSAGES_COLOR
                                                                : 'yellowgreen',
                                                        }}
                                                        className={style.receive_messages_value_background}
                                                    >
                                                        {caregiver.fields.receiveMessages ? 'Yes' : 'No'}
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            );
                        },
                    },
                ]}
                // only include youth that are not in the ineligible list
                data={youthList.filter(
                    youth => !getInegligibleYouth(youthList).some(ineligible => ineligible.pawnId === youth.pawnId)
                )}
                isSimpleCheckboxes
                showRowActions={false}
                hideShowingRowsSummary
                showPerPageOptions={false}
                rowSelectedCallback={selected => {
                    let res = [];

                    youthList.forEach(youth => {
                        const pawnId = _get(youth, 'pawnId', '-1').toString();
                        if (selected.includes(pawnId)) {
                            let caregiverPawnIds = [];
                            youth.caregiverList.forEach(caregiver => {
                                // include all caregivers, even if they have receiveMessages=false ; This is in case they decide to start sending messages in the middle of plan (e.g. got consent after week 2 - message will then start to be send but previous/missed messages will not be sent).
                                caregiverPawnIds.push(caregiver.pawn.pawnId);
                            });

                            res.push({ youthPawnId: youth.pawnId, caregiverPawnIds });
                        }
                    });

                    setSelectedYouth(prev => {
                        if (prev && res.length !== prev.length) {
                            return res;
                        }
                        return prev;
                    });
                }}
                rowActions={[
                    {
                        name: 'placeholder',
                        title: 'placeholder',
                        action: () => {},
                    },
                ]}
            />

            <br />

            <div key="added">
                <div className={style.tooltip_text}>
                    Youth who are ineligible to receive messages are listed in the table below.
                    <br />
                    Message settings can be changed on a per-user level in the{' '}
                    <a href="/manage">
                        <u>Manage</u>
                    </a>{' '}
                    page.
                </div>
                <HiddenBlock title="Ineligible">
                    <Table
                        rowKey="pawnId"
                        columns={[
                            {
                                name: 'wrapId',
                                label: 'Youth Wrap ID',
                                sort: true,
                                filter: true,
                            },
                            {
                                name: 'ineligible_reason',
                                label: 'Reason',
                                sort: true,
                                filter: true,
                            },
                            {
                                name: 'firstName',
                                label: 'Youth First Name',
                                sort: true,
                                filter: true,
                            },
                            {
                                name: 'lastName',
                                label: 'Youth Last Name',
                                sort: true,
                                filter: true,
                            },
                            {
                                name: 'receiveMessages',
                                label: 'Youth Receives Messages',
                                sort: true,
                                render: val => {
                                    return (
                                        <Tooltip
                                            title="Message settings can be changed for each user when editing them in the Manage tab."
                                            placement="top"
                                        >
                                            <div
                                                style={{
                                                    background: !val ? DOES_NOT_RECEIVE_MESSAGES_COLOR : 'yellowgreen',
                                                }}
                                                className={style.receive_messages_value_background}
                                            >
                                                {val ? 'Yes' : 'No'}
                                            </div>
                                        </Tooltip>
                                    );
                                },
                            },
                            {
                                name: 'youthStatus',
                                label: 'Youth Status',
                                sort: true,
                                filter: true,
                            },
                            {
                                name: 'caregiverList',
                                label: 'Caregiver(s)',
                                sort: true,
                                filter: true,
                                render: val => {
                                    return (
                                        <ul>
                                            {val.map((caregiver, j) => (
                                                <li key={j}>
                                                    <div>
                                                        {`${caregiver.fields.firstName} ${caregiver.fields.lastName} (${caregiver.fields.wrapId})`}
                                                    </div>
                                                    <div>
                                                        Receives Messages:{' '}
                                                        <Tooltip
                                                            title="Message settings can be changed for each user when editing them in the Manage tab."
                                                            placement="top"
                                                        >
                                                            <span
                                                                style={{
                                                                    background: !caregiver.fields.receiveMessages
                                                                        ? DOES_NOT_RECEIVE_MESSAGES_COLOR
                                                                        : 'yellowgreen',
                                                                }}
                                                                className={style.receive_messages_value_background}
                                                            >
                                                                {caregiver.fields.receiveMessages ? 'Yes' : 'No'}
                                                            </span>
                                                        </Tooltip>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    );
                                },
                            },
                        ]}
                        data={getInegligibleYouth(youthList)}
                        showRowActions={false}
                        hideShowingRowsSummary
                        showPerPageOptions={false}
                        rowActions={[]}
                    />
                </HiddenBlock>
            </div>
        </div>
    );
};

export default YouthSelectionTable;
